import type { I18n } from '@learnforge/i18n';
import { createSvgEl } from '@learnforge/demo-render';

export interface SampleSpaceGridOptions {
  kind: '2d6-grid' | '2d6-sum' | '1d6' | 'coin-flips' | 'card-suit';
  /** Required when kind === 'coin-flips'. */
  flips?: number;
  selected?: readonly number[];
  onToggle(outcomeIndex: number): void;
  i18n: I18n<string>;
}

export interface SampleSpaceGridHandle {
  group: SVGGElement;
  setSelected(indices: readonly number[]): void;
  destroy(): void;
}

const CELL = 50;
const PAD = 10;

export function createSampleSpaceGrid(
  parent: SVGGElement,
  opts: SampleSpaceGridOptions,
): SampleSpaceGridHandle {
  const group = createSvgEl('g', { class: 'pb-ss-grid' });
  parent.appendChild(group);
  let selected = new Set<number>(opts.selected ?? []);

  function render(): void {
    group.replaceChildren();
    const labels = computeOutcomeLabels(opts);
    const cols = computeColumnsForKind(opts);
    for (let i = 0; i < labels.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const rect = createSvgEl('rect', {
        class: `pb-ss-cell ${selected.has(i) ? 'pb-ss-cell-selected' : ''}`.trim(),
        x: PAD + col * CELL,
        y: PAD + row * CELL,
        width: CELL - 2,
        height: CELL - 2,
        rx: 4,
      });
      rect.addEventListener('click', () => {
        if (selected.has(i)) selected.delete(i);
        else selected.add(i);
        rect.setAttribute(
          'class',
          `pb-ss-cell ${selected.has(i) ? 'pb-ss-cell-selected' : ''}`.trim(),
        );
        opts.onToggle(i);
      });
      group.appendChild(rect);
      const txt = createSvgEl('text', {
        class: 'pb-ss-cell-label',
        x: PAD + col * CELL + (CELL - 2) / 2,
        y: PAD + row * CELL + (CELL - 2) / 2,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      });
      txt.textContent = labels[i]!;
      group.appendChild(txt);
    }
  }

  render();

  return {
    group,
    setSelected(indices) {
      selected = new Set(indices);
      render();
    },
    destroy() {
      group.remove();
    },
  };
}

function computeOutcomeLabels(opts: SampleSpaceGridOptions): string[] {
  switch (opts.kind) {
    case '2d6-grid': {
      const out: string[] = [];
      for (let i = 1; i <= 6; i++) for (let j = 1; j <= 6; j++) out.push(`${i},${j}`);
      return out;
    }
    case '2d6-sum':
      return Array.from({ length: 11 }, (_, i) => String(i + 2));
    case '1d6':
      return ['1', '2', '3', '4', '5', '6'];
    case 'coin-flips': {
      const n = opts.flips ?? 2;
      const labels: string[] = [];
      const total = 2 ** n;
      for (let i = 0; i < total; i++) {
        let s = '';
        for (let b = n - 1; b >= 0; b--) s += (i >> b) & 1 ? 'T' : 'H';
        labels.push(s);
      }
      return labels;
    }
    case 'card-suit':
      return ['♠', '♥', '♦', '♣'];
  }
}

function computeColumnsForKind(opts: SampleSpaceGridOptions): number {
  switch (opts.kind) {
    case '2d6-grid':
      return 6;
    case '2d6-sum':
      return 11;
    case '1d6':
      return 6;
    case 'coin-flips':
      return Math.min(8, 2 ** (opts.flips ?? 2));
    case 'card-suit':
      return 4;
  }
}
